import React, { useState, useRef } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { Dialog } from 'primereact/dialog';
import * as saveSvgAsPng from 'save-svg-as-png';
import { Eye, FileText, Image } from 'lucide-react';
import EquipmentDetailsModal from './EquipmentDetailsModal';

const EquipmentOrderingStatus = ({
  selectedCategory,
  setSelectedCategory,
  data,
  loading,
  departments,
  selectedDepartment,
  setSelectedDepartment,
  equipmentList,
  selectedEquipment,
  setSelectedEquipment
}) => {
  const [showEquipmentDetails, setShowEquipmentDetails] = useState(false);
  const [equipmentDetailsTab, setEquipmentDetailsTab] = useState('delivered');
  const [showDetails, setShowDetails] = useState(false);
  const chartRef = useRef(null);
  
  const selectedDepartmentName = departments.find(dep => dep.id === parseInt(selectedDepartment))?.name || 'Equipment';

  const handleDownloadCSV = () => {
    const dataToDownload = data.map(item => ({
      ship: item.ship,
      delivered: item.delivered ?? '',
      notDelivered: item.notDelivered ?? '',
      notOrdered: item.notOrdered ?? '',
      total: (item.delivered + item.notDelivered + item.notOrdered) ?? ''
    }));

    const csvContent = [
      ['Ship', 'Delivered', 'Not Delivered', 'Not Ordered', 'Total'].join(','),
      ...dataToDownload.map(item => [
        item.ship,
        item.delivered,
        item.notDelivered,
        item.notOrdered,
        item.total
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `equipment-ordering-status-${selectedDepartmentName.replace(/\s+/g, '-').toLowerCase()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPNG = () => {
  const chartContainer = chartRef.current?.querySelector('.recharts-wrapper');
  if (chartContainer) {
    const svg = chartContainer.querySelector('svg');
    if (svg) {
      // Clone the SVG to avoid modifying the original
      const clonedSvg = svg.cloneNode(true);
      
      // Get current SVG dimensions
      const svgRect = svg.getBoundingClientRect();
      const originalWidth = svgRect.width;
      const originalHeight = svgRect.height;
      
      // Create legend data
      const legendData = [
        { name: 'Delivered', color: '#4ade80' },
        { name: 'Not Delivered', color: '#facc15' },
        { name: 'Not Ordered', color: '#ef4444' }
      ];
      
      // Calculate new dimensions to accommodate legend
      const legendHeight = 60; // Space for legend
      const newHeight = originalHeight + legendHeight;
      
      // Update SVG dimensions
      clonedSvg.setAttribute('width', originalWidth);
      clonedSvg.setAttribute('height', newHeight);
      clonedSvg.setAttribute('viewBox', `0 0 ${originalWidth} ${newHeight}`);
      
      // Create legend group
      const legendGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      legendGroup.setAttribute('transform', `translate(0, ${originalHeight + 10})`);
      
      // Add legend background
      const legendBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      legendBg.setAttribute('x', '0');
      legendBg.setAttribute('y', '0');
      legendBg.setAttribute('width', originalWidth);
      legendBg.setAttribute('height', legendHeight - 10);
      legendBg.setAttribute('fill', '#ffffff');
      legendBg.setAttribute('stroke', '#e5e7eb');
      legendBg.setAttribute('stroke-width', '1');
      legendGroup.appendChild(legendBg);
      
      // Calculate legend item spacing
      const legendItemWidth = originalWidth / legendData.length;
      const legendStartX = legendItemWidth / 2;
      
      // Add legend items
      legendData.forEach((item, index) => {
        const x = legendStartX + (index * legendItemWidth);
        const y = 25;
        
        // Create legend item group
        const itemGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        
        // Add color rectangle
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', x - 60);
        rect.setAttribute('y', y - 8);
        rect.setAttribute('width', '16');
        rect.setAttribute('height', '16');
        rect.setAttribute('fill', item.color);
        rect.setAttribute('stroke', '#374151');
        rect.setAttribute('stroke-width', '1');
        itemGroup.appendChild(rect);
        
        // Add text label
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.setAttribute('x', x - 40);
        text.setAttribute('y', y + 4);
        text.setAttribute('font-family', 'Arial, sans-serif');
        text.setAttribute('font-size', '14');
        text.setAttribute('fill', '#374151');
        text.setAttribute('text-anchor', 'start');
        text.textContent = item.name;
        itemGroup.appendChild(text);
        
        legendGroup.appendChild(itemGroup);
      });
      
      // Add title to legend
      const titleText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      titleText.setAttribute('x', originalWidth / 2);
      titleText.setAttribute('y', 15);
      titleText.setAttribute('font-family', 'Arial, sans-serif');
      titleText.setAttribute('font-size', '12');
      titleText.setAttribute('font-weight', 'bold');
      titleText.setAttribute('fill', '#374151');
      titleText.setAttribute('text-anchor', 'middle');
      titleText.textContent = 'Legend';
      legendGroup.appendChild(titleText);
      
      // Append legend to cloned SVG
      clonedSvg.appendChild(legendGroup);
      
      // Convert to PNG using save-svg-as-png
      saveSvgAsPng.saveSvgAsPng(
        clonedSvg,
        `equipment-ordering-status-${selectedDepartmentName.replace(/\s+/g, '-').toLowerCase()}.png`,
        {
          scale: 2,
          backgroundColor: '#ffffff',
          width: originalWidth,
          height: newHeight
        }
      );
    }
  }
};

  const renderDetailsTable = () => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ship</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivered</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Not Delivered</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Not Ordered</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivery Rate (%)</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item, index) => {
              const total = item.delivered + item.notDelivered + item.notOrdered;
              const deliveryRate = total > 0 ? ((item.delivered / total) * 100).toFixed(1) : '0';
              
              return (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.ship}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {item.delivered ?? '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {item.notDelivered ?? '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {item.notOrdered ?? '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{total}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{deliveryRate}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderChart = (category, height = 350) => (
    <div key={category} className="bg-white rounded-xl shadow-md p-4" ref={chartRef}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">{selectedDepartmentName}</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowDetails(true)}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
          >
            <Eye size={16} className="mr-1" />
            View Details
          </button>
          <button
            onClick={handleDownloadCSV}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center"
            title="Download CSV"
          >
            <FileText size={16} className="mr-1" />
            CSV
          </button>
          <button
            onClick={handleDownloadPNG}
            className="text-sm text-gray-600 hover:text-gray-800 font-medium flex items-center"
            title="Download PNG"
          >
            <Image size={16} className="mr-1" />
            PNG
          </button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-64 text-gray-500">Loading equipment data...</div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis dataKey="ship" />
            <YAxis />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-white p-3 rounded shadow border border-gray-200">
                      <p className="font-semibold">{label}</p>
                      {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }}>
                          {entry.name}: {entry.value}
                        </p>
                      ))}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Bar dataKey="delivered" stackId="a" fill="#4ade80" name="Delivered" />
            <Bar dataKey="notDelivered" stackId="a" fill="#facc15" name="Not Delivered" />
            <Bar dataKey="notOrdered" stackId="a" fill="#ef4444" name="Not Ordered" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );

  return (
    <>
      <div className="flex justify-start gap-4 mb-4">
        <div className="flex items-center gap-2">
          <label htmlFor="department-select" className="text-sm font-medium text-gray-700">Department:</label>
          <select
            id="department-select"
            value={selectedDepartment}
            onChange={e => setSelectedDepartment(e.target.value)}
            className="form-select rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 text-sm bg-white w-[200px]"
          >
            <option value="">Select Department</option>
            {departments.map(dep => (
              <option key={dep.id} value={dep.id}>{dep.name}</option>
            ))}
          </select>
        </div>
      </div>

      <Dialog
        visible={showDetails}
        onHide={() => setShowDetails(false)}
        header={`Equipment Ordering Status - ${selectedDepartmentName}`}
        className="w-full max-w-7xl"
        draggable={false}
      >
        {renderDetailsTable()}
      </Dialog>

      {renderChart(selectedCategory)}
      
      <EquipmentDetailsModal
        show={showEquipmentDetails}
        onClose={() => setShowEquipmentDetails(false)}
        activeTab={equipmentDetailsTab}
        setActiveTab={setEquipmentDetailsTab}
        data={data?.equipment || []}
        departmentName={selectedDepartmentName}
      />
    </>
  );
};

export default EquipmentOrderingStatus;